import { useState, useEffect, useCallback } from "react"
import { useNavigate, useLocation } from "react-router-dom"
import { supabase } from "@/integrations/supabase/client"
import { Button } from "@/components/ui/button"
import { toast } from "sonner"
import { ReviewForm } from "@/components/ReviewForm"

export default function NewReview() {
  const navigate = useNavigate()
  const location = useLocation()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [formData, setFormData] = useState({
    airport_name: "",
    airport_location: "",
    review_text: "",
    film_friendly_score: 1,
    hand_check_success: true,
    latitude: "",
    longitude: "",
    terminal: "",
    hand_check_date: "",
  })

  const previousAirportCode = location.state?.previousAirportCode
  const airportId = location.state?.airport?.id

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!session) {
        navigate("/auth")
        return
      }
    })
  }, [navigate])

  const handleAirportSelect = useCallback(async (airport: { name: string; location: string; id: number; iata_code: string | null }) => {
    try {
      const { data: airportData, error } = await supabase
        .from('airports')
        .select('latitude, longitude')
        .eq('id', airport.id)
        .single()

      if (error) {
        console.error("Error fetching airport coordinates:", error)
        return
      }

      setFormData(prev => ({
        ...prev,
        airport_name: airport.name,
        airport_location: airport.location,
        latitude: airportData?.latitude?.toString() || "",
        longitude: airportData?.longitude?.toString() || "",
      }))
    } catch (error) {
      console.error("Error in handleAirportSelect:", error)
    }
  }, [])

  useEffect(() => {
    const state = location.state as { airport?: { id: number; name: string; location: string } }
    if (state?.airport) {
      handleAirportSelect({
        id: state.airport.id,
        name: state.airport.name,
        location: state.airport.location,
        iata_code: null
      })
    }
  }, [location, handleAirportSelect])

  const handleFormDataChange = (newData: Partial<typeof formData>) => {
    setFormData(prev => ({ ...prev, ...newData }))
  }

  const handleCancel = () => {
    if (previousAirportCode) {
      navigate(`/airport/${previousAirportCode}`)
    } else if (airportId) {
      supabase
        .from('airports')
        .select('iata_code')
        .eq('id', airportId)
        .single()
        .then(({ data }) => {
          if (data?.iata_code) {
            navigate(`/airport/${data.iata_code}`)
          } else {
            navigate("/")
          }
        })
        .then(undefined, (error) => {
          console.error("Error fetching airport code:", error)
          navigate("/")
        })
    } else {
      navigate("/")
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!formData.airport_name || !formData.airport_location) {
      toast.error("Please select an airport before submitting the review")
      return
    }

    setIsSubmitting(true)

    try {
      const { data: { session }, error: authError } = await supabase.auth.getSession()
      
      if (authError || !session) {
        throw new Error("You must be logged in to submit a review")
      }

      const { error } = await supabase.from("reviews").insert({
        airport_name: formData.airport_name,
        airport_location: formData.airport_location,
        review_text: formData.review_text,
        film_friendly_score: parseInt(formData.film_friendly_score.toString()),
        hand_check_success: formData.hand_check_success,
        latitude: formData.latitude ? parseFloat(formData.latitude) : null,
        longitude: formData.longitude ? parseFloat(formData.longitude) : null,
        terminal: formData.terminal || null,
        hand_check_date: formData.hand_check_date || new Date().toISOString(),
        user_id: session.user.id
      })

      if (error) throw error

      toast.success("Review added successfully!")
      
      if (previousAirportCode) {
        navigate(`/airport/${previousAirportCode}`)
      } else {
        navigate("/")
      }
    } catch (error) {
      console.error("Error:", error)
      toast.error(error instanceof Error ? error.message : "Failed to add review")
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="container max-w-2xl mx-auto px-4 py-8">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold">Add Airport Review</h1>
        <Button variant="outline" onClick={handleCancel}>
          Cancel
        </Button>
      </div>

      <ReviewForm
        formData={formData}
        isSubmitting={isSubmitting}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        onAirportSelect={handleAirportSelect}
        onFormDataChange={handleFormDataChange}
      />
    </div>
  )
}
